import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  Zap, Mail, Lock, Eye, EyeOff, User, Building2, ArrowRight, CheckCircle,
} from 'lucide-react'
import { Button } from '../../components/ui/Button'
import { LanguageSwitch } from '../../i18n/LanguageSwitch'
import { useLocale } from '../../i18n/LocaleProvider'
import { signUp, signIn, getCognitoErrorMessage } from '../../lib/cognito'
import { setTokens } from '../../lib/auth'
import { bootstrapAuthenticatedUser, logoutAfterBootstrapFailure } from '../../lib/authBootstrap'
import { normalizeEmail } from '../../lib/emailVerification'
import { EmailVerificationForm } from './EmailVerificationForm'
import { WorkspaceBootstrapErrorView } from './WorkspaceBootstrapError'
import useAuthStore from '../../store/authStore'
import useOrgStore from '../../store/orgStore'

function PasswordStrength({ password, t }) {
  const checks = [
    { label: t('auth.signUp.strength.minimum'), pass: password.length >= 8 },
    { label: t('auth.signUp.strength.uppercase'), pass: /[A-Z]/.test(password) },
    { label: t('auth.signUp.strength.lowercase'), pass: /[a-z]/.test(password) },
    { label: t('auth.signUp.strength.number'), pass: /[0-9]/.test(password) },
  ]

  const score = checks.filter((c) => c.pass).length
  const colors = ['bg-red-400', 'bg-orange-400', 'bg-yellow-400', 'bg-green-500']
  const labels = [
    t('auth.signUp.strength.weak'),
    t('auth.signUp.strength.fair'),
    t('auth.signUp.strength.good'),
    t('auth.signUp.strength.strong'),
  ]

  if (!password) return null

  return (
    <div className="mt-2">
      <div className="flex gap-1 mb-1.5">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className={`h-1 flex-1 rounded-full transition-all ${i < score ? colors[score - 1] : 'bg-gray-200'}`} />
        ))}
      </div>
      <div className="flex flex-wrap gap-x-3 gap-y-1">
        {checks.map((check) => (
          <span key={check.label} className={`text-xs flex items-center gap-1 ${check.pass ? 'text-green-600' : 'text-gray-400'}`}>
            <CheckCircle className={`w-3 h-3 ${check.pass ? 'text-green-500' : 'text-gray-300'}`} />
            {check.label}
          </span>
        ))}
        {score > 0 && (
          <span className={`text-xs font-semibold ml-auto ${score === 4 ? 'text-green-600' : score >= 2 ? 'text-yellow-600' : 'text-red-500'}`}>
            {labels[score - 1]}
          </span>
        )}
      </div>
    </div>
  )
}

export function SignUp() {
  const navigate = useNavigate()
  const { t } = useLocale()
  const { loadUser } = useAuthStore()
  const { fetchOrg, fetchUsage } = useOrgStore()

  const [step, setStep] = useState('signup')
  const [name, setName] = useState('')
  const [companyName, setCompanyName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [bootstrapError, setBootstrapError] = useState(false)

  async function runBootstrap(verifiedEmail) {
    const result = await bootstrapAuthenticatedUser({
      email: verifiedEmail,
      name: name.trim(),
      companyName: companyName.trim(),
      destination: '/dashboard',
    })

    await loadUser()
    await fetchOrg()
    await fetchUsage()

    if (result.warning) {
      alert(result.warning)
    }
    navigate(result.destination || '/dashboard')
  }

  async function handleSignUp(e) {
    e.preventDefault()
    setError('')

    if (!name.trim()) { setError(t('auth.errors.requiredName')); return }
    const normalizedEmail = normalizeEmail(email)

    if (!normalizedEmail) { setError(t('auth.errors.requiredEmail')); return }
    if (password.length < 8) { setError(t('auth.errors.passwordMinimum')); return }
    if (password !== confirmPassword) { setError(t('auth.errors.passwordMismatch')); return }

    setLoading(true)
    try {
      await signUp(normalizedEmail, password, name.trim(), companyName.trim())
      setEmail(normalizedEmail)
      setStep('verify')
    } catch (err) {
      setError(getCognitoErrorMessage(err, t))
    }
    setLoading(false)
  }

  async function handleVerified(verifiedEmail) {
    setError('')
    let signedIn = false

    try {
      const { idToken, refreshToken } = await signIn(verifiedEmail, password)
      setTokens(idToken, refreshToken)
      signedIn = true
      await runBootstrap(verifiedEmail)

    } catch (err) {
      const message = getCognitoErrorMessage(err, t)
      setError(message)
      if (signedIn) {
        setBootstrapError(true)
      }
      throw new Error(message)
    }
  }

  async function handleRetryBootstrap() {
    const normalizedEmail = normalizeEmail(email)
    setLoading(true)
    setError('')
    try {
      await runBootstrap(normalizedEmail)
    } catch {
      setBootstrapError(true)
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-brand-50 via-white to-blue-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="mb-4 flex justify-end">
          <LanguageSwitch />
        </div>

        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-8">
          <div className="inline-flex items-center gap-3 cursor-pointer" onClick={() => navigate('/')}>
            <div className="w-12 h-12 rounded-2xl bg-brand-600 flex items-center justify-center shadow-lg">
              <Zap className="w-6 h-6 text-white" />
            </div>
            <div className="text-left">
              <p className="font-bold text-gray-900 text-xl leading-tight">WorkflowPro</p>
              <p className="text-gray-500 text-sm leading-tight">{t('auth.brandTagline')}</p>
            </div>
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="card p-8">

          {bootstrapError ? (
            <WorkspaceBootstrapErrorView
              loading={loading}
              onRetry={handleRetryBootstrap}
              onLogout={logoutAfterBootstrapFailure}
            />
          ) : step === 'signup' ? (
            <>
              <div className="mb-6">
                <h1 className="text-2xl font-bold text-gray-900">{t('auth.signUp.title')}</h1>
                <p className="text-gray-500 mt-1">{t('auth.signUp.subtitle')}</p>
              </div>

              {error && (
                <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="bg-red-50 border border-red-200 rounded-xl p-3 mb-5">
                  <span className="text-red-600 text-sm font-medium">{error}</span>
                </motion.div>
              )}

              <form onSubmit={handleSignUp} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="form-group">
                    <label className="label">{t('auth.signUp.name')}</label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input type="text" className="input pl-10" placeholder={t('auth.signUp.namePlaceholder')} value={name} onChange={(e) => setName(e.target.value)} autoFocus />
                    </div>
                  </div>
                  <div className="form-group">
                    <label className="label">{t('auth.signUp.company')}</label>
                    <div className="relative">
                      <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input type="text" className="input pl-10" placeholder={t('auth.signUp.companyPlaceholder')} value={companyName} onChange={(e) => setCompanyName(e.target.value)} />
                    </div>
                  </div>
                </div>

                <div className="form-group">
                  <label className="label">{t('auth.signUp.workEmail')}</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input type="email" className="input pl-10" placeholder={t('auth.common.emailPlaceholder')} value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" />
                  </div>
                </div>

                <div className="form-group">
                  <label className="label">{t('auth.common.password')}</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input type={showPassword ? 'text' : 'password'} className="input pl-10 pr-10" placeholder={t('auth.signUp.createPassword')} value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" />
                    <button type="button" onClick={() => setShowPassword(!showPassword)} aria-label={t(showPassword ? 'auth.common.hidePassword' : 'auth.common.showPassword')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  <PasswordStrength password={password} t={t} />
                </div>

                <div className="form-group">
                  <label className="label">{t('auth.signUp.confirmPassword')}</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input type={showPassword ? 'text' : 'password'} className="input pl-10" placeholder={t('auth.signUp.repeatPassword')} value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} autoComplete="new-password" />
                  </div>
                  {confirmPassword && password !== confirmPassword && <p className="error-text">{t('auth.errors.passwordMismatch')}</p>}
                </div>

                <Button type="submit" variant="primary" fullWidth size="lg" loading={loading} icon={<ArrowRight className="w-4 h-4" />} iconPosition="right">
                  {t('auth.signUp.createAccount')}
                </Button>

                <p className="text-xs text-center text-gray-400">
                  {t('auth.signUp.legalPrefix')}{' '}
                  <Link to="/terms" className="text-brand-600 hover:text-brand-700 font-medium">
                    {t('auth.signIn.terms')}
                  </Link>
                  {' '}{t('auth.signIn.and')}{' '}
                  <Link to="/privacy" className="text-brand-600 hover:text-brand-700 font-medium">
                    {t('auth.signIn.privacy')}
                  </Link>
                </p>
              </form>

              <div className="mt-6 pt-6 border-t border-gray-100 text-center">
                <p className="text-sm text-gray-500">
                  {t('auth.signUp.alreadyAccount')}{' '}
                  <button onClick={() => navigate('/signin')} className="text-brand-600 hover:text-brand-700 font-semibold">{t('auth.common.signIn')}</button>
                </p>
              </div>
            </>

          ) : (
            <>
              {error && (
                <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="bg-red-50 border border-red-200 rounded-xl p-3 mb-5">
                  <span className="text-red-600 text-sm font-medium">{error}</span>
                </motion.div>
              )}

              <EmailVerificationForm
                email={email}
                onConfirmed={handleVerified}
                submitLabel={t('auth.signUp.verifyAndStart')}
              />
            </>
          )}

        </motion.div>
      </div>
    </div>
  )
}
